import type { FieldPath, FieldValues } from 'react-hook-form'
import type { CSSProperties } from 'react'
import {
  RadioButtonGroup,
  type RadioButtonGroupOption,
} from '../../radioButton/RadioButtonGroup'
import { FormField } from '../FormField'
import { InputField } from '../../input/InputField'
import { useFieldRequired } from '../formSchemaContext'

export type RadioGroupFieldProps<
  T extends FieldValues = FieldValues,
  V extends string = string,
> = {
  name: FieldPath<T>
  options: RadioButtonGroupOption<V>[]
  label?: string
  tooltipText?: string
  required?: boolean
  direction?: 'row' | 'column'
  labelPosition?: 'left' | 'right'
  labelSize?: 'medium' | 'small'
  disabled?: boolean
  style?: CSSProperties
  radioButtonStyle?: CSSProperties
}

export const RadioGroupField = <
  T extends FieldValues = FieldValues,
  V extends string = string,
>({
  name,
  label,
  tooltipText,
  required,
  options,
  direction = 'column',
  labelPosition,
  labelSize,
  disabled,
  style,
  radioButtonStyle,
}: RadioGroupFieldProps<T, V>) => {
  const fieldRequired = useFieldRequired(name)
  const isRequired = required ?? fieldRequired

  return (
    <FormField<T>
      name={name}
      render={({ value, onChange, errorMessage }) => (
        <InputField
          label={label}
          labelStyle={{ color: 'var(--palette-blue-500)' }}
          tooltipText={tooltipText}
          required={isRequired}
          errorMessage={errorMessage}
        >
          <RadioButtonGroup<V>
            options={options}
            selectedOption={(value as V) ?? undefined}
            onClick={onChange}
            direction={direction}
            labelPosition={labelPosition}
            labelSize={labelSize}
            disabled={disabled}
            style={style}
            radioButtonStyle={radioButtonStyle}
          />
        </InputField>
      )}
    />
  )
}
